import { useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import "../styles/play-landing.css";

const SPORTS = [
  "WTA Miami Open", "Premier League", "ATP Roland Garros",
  "Champions League", "Wimbledon", "World Cup", "US Open", "Euro 2028",
];

export default function PlayLanding() {
  useEffect(() => {
    document.title = "Sport X — Play";
    return () => { document.title = "Sport X"; };
  }, []);

  const scrollToHow = useCallback((e) => {
    e.preventDefault();
    const el = document.getElementById("play-how");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  }, []);

  return (
    <div className="play-landing" data-testid="play-landing-page">

      {/* ===== NAV ===== */}
      <nav className="play-nav" data-testid="play-nav">
        <Link to="/" className="play-nav-logo">SPORT <span>X</span></Link>
        <div className="play-nav-links play-nav-links-desktop">
          <a href="#play-how" onClick={scrollToHow}>How it works</a>
          <Link to="/eitc">For clubs</Link>
          <Link to="/" className="play-nav-cta">Play now</Link>
        </div>
        <Link to="/" className="play-nav-cta play-nav-cta-mobile">Play now</Link>
      </nav>

      {/* ===== HERO ===== */}
      <section className="play-hero" data-testid="play-hero">
        <div className="play-hero-grid" />
        <div className="play-hero-glow" />
        <div className="play-hero-content">
          <div className="play-hero-badge">
            <span className="play-hero-badge-dot" />
            Live auctions &middot; Real sport &middot; No gambling
          </div>

          <h1 className="play-h1">
            The game<br />
            sport fans<br />
            <span className="play-h1-accent">actually</span><br />
            want
          </h1>

          <p className="play-hero-sub">
            Bid on real players. Compete with friends. Follow live results.<br />
            <strong>No betting. No subscriptions. No endless tinkering.</strong><br />
            Just the sport you love, played together.
          </p>

          {/* Auction Preview */}
          <div className="play-ap" data-testid="play-auction-preview">
            <div className="play-ap-live">LIVE</div>
            <div className="play-ap-timer">00:19</div>
            <div className="play-ap-player">Aryna Sabalenka</div>
            <div className="play-ap-seed">&#127463;&#127486; Belarus &middot; Seed 1 &middot; WTA Miami Open</div>
            <div className="play-ap-bid-label">Current highest bid</div>
            <div className="play-ap-bid">&pound;15m</div>
            <div className="play-ap-leader">daz6 leading</div>
            <div className="play-ap-btns">
              <div className="play-ap-btn">+&pound;1m</div>
              <div className="play-ap-btn">+&pound;5m</div>
              <div className="play-ap-btn">+&pound;10m</div>
              <div className="play-ap-btn">+&pound;20m</div>
              <div className="play-ap-btn">+&pound;50m</div>
              <div className="play-ap-btn play-ap-pass">Pass</div>
            </div>
            <div className="play-ap-participants">
              <div className="play-ap-avatar" style={{ background: "#1a3a5c", color: "#22d3ee" }}>DA</div>
              <div className="play-ap-avatar" style={{ background: "#1a2e1a", color: "#4ade80" }}>GO</div>
              <div className="play-ap-avatar" style={{ background: "#3a1a2e", color: "#f472b6" }}>SK</div>
              <div className="play-ap-count">3 of 6 participants &middot; 2 online</div>
            </div>
          </div>

          <div className="play-hero-btns">
            <Link to="/" className="play-btn-primary" data-testid="play-cta-start">Start playing free</Link>
            <a href="#play-how" onClick={scrollToHow} className="play-btn-secondary">How it works</a>
          </div>
          <p className="play-not-gambling"><span>No real money.</span> No gambling. No app to download. Free to play.</p>
        </div>
      </section>

      {/* ===== SPORTS SCROLL ===== */}
      <div className="play-sports-strip">
        <div className="play-sports-scroll">
          {[...SPORTS, ...SPORTS].map((s, i) => (
            <div className="play-sport-pill" key={i}>
              {i % 2 === 0 ? "\uD83C\uDFBE" : "\u26BD"} {s}
            </div>
          ))}
        </div>
      </div>

      {/* ===== HOW IT WORKS ===== */}
      <section className="play-section" id="play-how" data-testid="play-how-it-works">
        <div className="play-section-label">How it works</div>
        <div className="play-section-title">Four steps.<br />One great night.</div>
        <div className="play-steps">
          <Step n="1" icon="&#127963;&#65039;" title="Create a challenge">
            Pick a sport and tournament. Give your challenge a name. Invite your group with one link — no account needed to join.
          </Step>
          <Step n="2" icon="&#9889;" title="Live auction">
            Everyone gets a virtual budget. Bid against each other for real players in a live, real-time auction. Thirty seconds per player. Pure adrenaline.
          </Step>
          <Step n="3" icon="&#128202;" title="Follow the results">
            Scores update automatically from real match results. Watch your squad climb the leaderboard — or collapse. No tinkering required.
          </Step>
          <Step n="4" icon="&#127942;" title="Winner takes glory">
            At the end of the tournament, the best squad wins. Bragging rights. Maybe a donation to the winner's chosen good cause. The conversation that runs for weeks.
          </Step>
        </div>
      </section>

      {/* ===== WHY SPORT X ===== */}
      <div className="play-strip" data-testid="play-why-strip">
        <div className="play-section-label" style={{ marginBottom: ".75rem" }}>Why Sport X</div>
        <div className="play-strip-title">
          <span className="play-strip-no">No gambling.</span><br />
          <span className="play-strip-yes">All game.</span>
        </div>
        <div className="play-strip-grid">
          <StripItem icon="&#128683;" type="no" title="No real money" desc="Virtual budgets only. The thrill with none of the risk." />
          <StripItem icon="&#128683;" type="no" title="No weekly management" desc="Bid once. Follow the results. That's it." />
          <StripItem icon="&#128683;" type="no" title="No app to download" desc="Works on any phone or laptop, straight from a link." />
          <StripItem icon="&#10003;" type="yes" title="Real players. Real results." desc="Scores update live from official data feeds." />
          <StripItem icon="&#10003;" type="yes" title="Private groups" desc="Your mates only. No strangers, no public leaderboards." />
          <StripItem icon="&#10003;" type="yes" title="Play for a cause" desc="Contribute to a club or charity if you want to. 90% goes straight there." />
        </div>
      </div>

      {/* ===== CTA ===== */}
      <section className="play-cta" data-testid="play-cta">
        <div className="play-cta-glow" />
        <div className="play-section-label">Ready?</div>
        <h2>Start your<br /><span>challenge</span><br />today</h2>
        <p>Free to set up. Free to play. Takes five minutes to create and share with your group. No excuses.</p>
        <Link
          to="/"
          className="play-btn-primary"
          style={{ fontSize: "1.1rem", padding: "1rem 3rem" }}
          data-testid="play-cta-play-btn"
        >
          Play now — it's free
        </Link>
        <div className="play-cta-sub">
          Running a club or charity? <Link to="/eitc">See how Sport X can raise funds for you &rarr;</Link>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="play-footer" data-testid="play-footer">
        <div className="play-footer-logo">SPORT <span>X</span></div>
        <p>&copy; 2026 Sport X is a Jana platform. Not a gambling product. No real money involved.</p>
        <div className="play-footer-links">
          <Link to="/about">About</Link>
          <Link to="/help">Help</Link>
          <Link to="/eitc">For clubs</Link>
          <Link to="/eitc">For charities</Link>
        </div>
      </footer>

      {/* ===== MOBILE BOTTOM NAV ===== */}
      <div className="play-nav-bottom" data-testid="play-mobile-nav">
        <a href="#play-how" onClick={scrollToHow}>How it works</a>
        <Link to="/eitc">For clubs</Link>
        <Link to="/" className="play-mob-cta">Play now</Link>
      </div>
    </div>
  );
}

/* ── Step Card ── */
function Step({ n, icon, title, children }) {
  return (
    <div className="play-step">
      <div className="play-step-num">{n}</div>
      <div className="play-step-icon">{icon}</div>
      <h3>{title}</h3>
      <p>{children}</p>
    </div>
  );
}

/* ── Strip Item ── */
function StripItem({ icon, type, title, desc }) {
  return (
    <div className="play-strip-item">
      <div className={`play-strip-icon play-icon-${type}`}>{icon}</div>
      <div className="play-strip-item-text">
        <strong>{title}</strong>
        <span>{desc}</span>
      </div>
    </div>
  );
}
